// Registro central de tipos de veículo para a inspeção 3D do checklist.
//
// Fundação da biblioteca de veículos (Fase 1, combinada com o usuário):
// cada veículo passa a ser um "recurso substituível" descrito aqui —
// categoria, arquivo .glb, ângulos de câmera, atribuição — em vez de
// constantes fixas espalhadas pelo componente. Adicionar ou trocar um
// veículo no futuro será: nova entrada aqui + arquivo .glb correspondente,
// sem tocar em VehicleInspection3D.jsx, no raycasting, na gravidade das
// avarias ou na persistência do checklist.
//
// IMPORTANTE: este arquivo ainda não é importado por nenhum outro módulo
// do sistema. É puramente aditivo — zero efeito no que já roda em produção.
// O modelo atual (car-concept.glb) continua servido exatamente como hoje,
// através de vehicleParts3d.js; a entrada abaixo apenas referencia essas
// mesmas constantes, sem duplicar nem alterar nada nesse arquivo.

import { CAMERA_DIRECTIONS, MODEL_ATTRIBUTION, MODEL_URL } from "./vehicleParts3d";

// Categorias planejadas para a biblioteca de veículos (definidas com o
// usuário). Ainda sem modelo 3D próprio — servem apenas para organizar a
// expansão futura. Nenhuma delas é usada em produção enquanto não existir
// uma entrada correspondente em VEHICLE_MODELS.
export const VEHICLE_CATEGORIES = [
  { key: "hatch_compacto", label: "Hatch Compacto" },
  { key: "hatch_medio", label: "Hatch Médio" },
  { key: "sedan_compacto", label: "Sedan Compacto" },
  { key: "sedan_medio", label: "Sedan Médio" },
  { key: "sedan_executivo", label: "Sedan Executivo" },
  { key: "suv_compacto", label: "SUV Compacto" },
  { key: "suv_medio", label: "SUV Médio" },
  { key: "suv_grande", label: "SUV Grande" },
  { key: "pickup_compacta", label: "Pickup Compacta" },
  { key: "pickup_media", label: "Pickup Média" },
  { key: "pickup_grande", label: "Pickup Grande" },
  { key: "van_utilitaria", label: "Van Utilitária" },
  { key: "furgao", label: "Furgão" },
  { key: "caminhao_leve", label: "Caminhão Leve" },
  { key: "caminhao_pesado", label: "Caminhão Pesado" },
  { key: "moto_street", label: "Motocicleta Street" },
  { key: "moto_trail", label: "Motocicleta Trail" },
  { key: "moto_esportiva", label: "Motocicleta Esportiva" },
];

// Id do modelo usado quando um checklist não tem tipo de veículo escolhido
// (todo checklist já existente cai aqui automaticamente — compatibilidade
// total, sem migração de dados necessária).
export const DEFAULT_MODEL_ID = "sedan_default_carconcept";

// Modelos 3D disponíveis hoje. Uma única entrada: o modelo atual,
// preservado sem nenhuma alteração de comportamento.
// Fase 2 — cor neutra: o próprio arquivo car-concept.glb já traz, de
// fábrica, variantes oficiais de pintura (extensão glTF padrão
// KHR_materials_variants: "Carmine Candy" = vermelho/atual, "Pearly Swirly"
// = prata/pérola, "Torched Graphite" = grafite escuro). Usamos os valores
// reais dos materiais "Paint 1/2 Graphite" já existentes no GLB (ajustados
// para um acabamento fosco -- roughness mais alto) como cor neutra padrão,
// a pedido do usuário (refinamento visual). Mapeado pelo nome do material
// original ("Paint 1/2 Carmine") -> aplicado em tempo de execução sobre o
// clone do material em VehicleInspection3D.jsx. Não afeta vidro, cromados,
// luzes, pneus ou o interior do veículo.
export const PAINT_NEUTRAL_OVERRIDE = {
  "Paint 1 Carmine": { color: [0.18, 0.18, 0.18], roughness: 0.75 },
  "Paint 2 Carmine": { color: [0.12, 0.12, 0.12], roughness: 0.7 },
};

// Fase 3 (validação técnica da arquitetura, combinada com o usuário):
// mapa nó-do-GLB -> peça e função de nós ocultos, agora parametrizados por
// modelo -- cada modelo tem os seus próprios (nomenclatura de malha é
// diferente em cada arquivo). O modelo padrão usa exatamente o
// NODE_TO_PARTE / isHiddenNode já existentes em vehicleParts3d.js
// (importados, não duplicados) -- comportamento 100% preservado.
import { NODE_TO_PARTE, isHiddenNode } from "./vehicleParts3d";

export const VEHICLE_MODELS = {
  [DEFAULT_MODEL_ID]: {
    categoria: "sedan_medio",
    label: "Sedan (modelo padrão atual)",
    modelUrl: MODEL_URL,
    cameraDirections: CAMERA_DIRECTIONS,
    attribution: MODEL_ATTRIBUTION,
    paintNeutralOverride: PAINT_NEUTRAL_OVERRIDE,
    nodeToParte: NODE_TO_PARTE,
    isHiddenNode,
  },

  // ── Candidatos de validação (Fase 3) ───────────────────────────────────
  // Modelos baixados do Sketchfab pelo usuário para testar a arquitetura
  // de substituição (malhas separadas, raycast, mapeamento de peças,
  // desempenho). NÃO são modelos definitivos de produção -- marcados
  // "dev: true" e nunca usados como DEFAULT_MODEL_ID. Logotipos/emblemas/
  // placas identificados foram ocultados (isHiddenNode), não apenas
  // ignorados.
  urban10_dev: {
    categoria: "hatch_medio",
    label: "Hatch urbano (DEV — validação de arquitetura, não usar em produção)",
    dev: true,
    modelUrl: "/models/urban_10_-_low_poly_model.glb",
    cameraDirections: CAMERA_DIRECTIONS,
    attribution:
      "Modelo de desenvolvimento (Sketchfab) — atribuição/licença final a confirmar antes de uso em produção.",
    paintNeutralOverride: {
      Urban10_bodymat: { color: [0.15, 0.15, 0.15], roughness: 0.75 },
    },
    nodeToParte: {
      Urban10_body_Urban10_bodymat_0: "body",
      Urban10_Bumper_Front_Urban10_bodymat_0: "front_bumper",
      Urban10_Bumper_Rear_Urban10_bodymat_0: "rear_bumper",
      Urban10_Headlights_UCB_Lights_and_Glass_0: "headlights",
      Urban10_Headlight_Glass_UCB_Lights_and_Glass_Transperent_0: "headlights",
      Urban10_blinkers_UCB_Lights_and_Glass_0: "headlights",
      Urban10_Brakelights_UCB_Lights_and_Glass_0: "taillights",
      Urban10_Reverse_UCB_Lights_and_Glass_0: "taillights",
      Urban10_Taillights_Glass_UCB_Lights_and_Glass_Transperent_0: "taillights",
      Urban10_Windshield_UCB_Lights_and_Glass_Transperent_0: "windshield",
      Urban10_Glass_Rear_UCB_Lights_and_Glass_Transperent_0: "rear_window",
      Urban10_Body_Glass_UCB_Lights_and_Glass_Transperent_0: "roof",
      Urban10_Glass_Driver_UCB_Lights_and_Glass_Transperent_0: "left_door",
      Urban10_Glass_Passenger_UCB_Lights_and_Glass_Transperent_0: "right_door",
      Urban10_WheelStock_FL_RB1c_Tire_1k_0: "wheel_front_left",
      Urban10_WheelStock_FR_RB1c_Tire_1k_0: "wheel_front_right",
      Urban10_WheelStock_RL_RB1c_Tire_1k_0: "wheel_rear_left",
      Urban10_WheelStock_RR_RB1c_Tire_1k_0: "wheel_rear_right",
    },
    // Oculta mecânica/interior (mesmo tratamento do modelo padrão) e,
    // principalmente, emblema/badge e placas -- a pedido do usuário, para
    // não exibir nenhuma identificação visual de marca. Antes as placas
    // eram uma peça clicável ("license_plate"); agora ficam ocultas, sem
    // alterar a geometria do arquivo (só visible=false em tempo de
    // execução, igual ao tratamento das peças mecânicas).
    isHiddenNode: (name) => {
      if (!name) return false;
      if (name.includes("Bottom")) return true;
      if (name.includes("Suspension")) return true;
      if (name.includes("Interior")) return true;
      if (name.includes("Steering_Wheel")) return true;
      if (name.includes("Bumper_Front_Inner")) return true;
      if (name.includes("Bumper_Rear_Inner")) return true;
      if (name.includes("Body_Badges")) return true; // emblema/logo -- ocultado
      if (name.includes("Numberplate")) return true; // placa -- ocultada
      return false;
    },
  },

  suv_generico_dev: {
    categoria: "suv_medio",
    label: "SUV genérico (DEV — validação de arquitetura, não usar em produção)",
    dev: true,
    modelUrl: "/models/lowpoly_generic_suv.glb",
    cameraDirections: CAMERA_DIRECTIONS,
    attribution:
      "Modelo de desenvolvimento (Sketchfab) — atribuição/licença final a confirmar antes de uso em produção.",
    paintNeutralOverride: {
      Body: { color: [0.15, 0.15, 0.15], roughness: 0.75 },
    },
    // Granularidade bem mais grosseira que o padrão: carroceria inteira é
    // uma única malha (sem capô/porta separados). Válido para testar que a
    // arquitetura também suporta um modelo simples sem quebrar nada -- só
    // não oferece marcação por peça tão fina quanto o modelo padrão.
    nodeToParte: {
      Body_Body_0: "body",
      Body_Texture_0: "body",
      Details_Texture_0: "body",
      Wheel_FL_Texture_0: "wheel_front_left",
      Wheel_FR_Texture_0: "wheel_front_right",
      Wheel_RL_Texture_0: "wheel_rear_left",
      Wheel_RR_Texture_0: "wheel_rear_right",
      Glass_Glass_0: "windshield",
    },
    isHiddenNode: (name) => {
      if (!name) return false;
      if (name.includes("Interior")) return true;
      if (name.includes("Shadow")) return true; // sombra falsa (decal), não é peça
      return false;
    },
  },
};

export function getModeloById(id) {
  return VEHICLE_MODELS[id] || VEHICLE_MODELS[DEFAULT_MODEL_ID];
}

export function getModelosPorCategoria(categoriaKey) {
  return Object.entries(VEHICLE_MODELS)
    .filter(([, m]) => m.categoria === categoriaKey)
    .map(([id, m]) => ({ id, ...m }));
}

// Fase 3: versão genérica de resolveParteKey/isHiddenNode (vehicleParts3d.js),
// parametrizada por modelo -- usada pelo VehicleInspection3D.jsx quando um
// modeloId é informado. Mesma lógica de subir a hierarquia de pais até
// achar um nó mapeado, só que consultando o nodeToParte do modelo ativo em
// vez do NODE_TO_PARTE fixo.
export function resolveParteKeyParaModelo(object3d, modelo) {
  let node = object3d;
  while (node) {
    const parte = modelo.nodeToParte[node.name];
    if (parte) return parte;
    node = node.parent;
  }
  return null;
}
